// Settings › AI assistants (spec dashboard/buyer-insights-chatgpt-connector.md §6): how to connect ChatGPT
// and Claude, questions to try, Make keys (admins), and the shop's keys with Block. Every user sees the
// tab; only admins create Make keys and block other people's keys.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useSearchParams } from 'react-router-dom';
import { dashboardAPI } from '../../../services/dashboard-api';
import { track } from '../../../utils/analytics';
import {
  CHATGPT_NOTES,
  CHATGPT_STEPS,
  CLAUDE_STEPS,
  CLAUDE_WARNING_STEP,
  HELP,
  INTRO,
  MAKE,
  MCP_URL,
  TRY_NOTE,
  blockQuestion,
  lastUsed,
  madeOn,
  stepParts,
  stoppedText,
  tryQuestions,
  visibleKeys,
} from './copy';

const copyText = (text) => navigator.clipboard?.writeText(text);

function CopyButton({ text, label }) {
  const [done, setDone] = useState(false);
  return (
    <Tooltip title={done ? 'Copied' : 'Copy'}>
      <IconButton
        size="small"
        aria-label={label || 'Copy'}
        onClick={() => {
          copyText(text);
          setDone(true);
          setTimeout(() => setDone(false), 2000);
        }}
      >
        <ContentCopyIcon fontSize="inherit" />
      </IconButton>
    </Tooltip>
  );
}

const Code = ({ children }) => (
  <Box component="code" sx={{ px: 0.5, bgcolor: 'grey.100', borderRadius: 0.5, fontSize: '0.9em', wordBreak: 'break-all' }}>
    {children}
  </Box>
);

function Step({ step }) {
  return (
    <>
      {stepParts(step).map((p, i) => {
        if (p.url)
          return (
            <span key={i}>
              <Code>{MCP_URL}</Code>
              <CopyButton text={MCP_URL} label="Copy the URL" />
            </span>
          );
        if (p.bold) return <strong key={i}>{p.bold}</strong>;
        if (p.code) return <Code key={i}>{p.code}</Code>;
        return <span key={i}>{p.text}</span>;
      })}
    </>
  );
}

function Steps({ steps, warning }) {
  return (
    <Box component="ol" sx={{ pl: 3, m: 0, '& li': { mb: 1 } }}>
      {steps.map((s, i) =>
        i === warning ? (
          <li key={i}>
            <Alert severity="warning" icon={false} sx={{ py: 0 }}>
              <Step step={s} />
            </Alert>
          </li>
        ) : (
          <li key={i}>
            <Step step={s} />
          </li>
        )
      )}
    </Box>
  );
}

function Panel({ title, children }) {
  return (
    <Accordion disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6" component="h2">
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  );
}

function KeyRow({ k, canEdit, onBlock }) {
  const stopped = Boolean(k.revoked_at);
  const mayBlock = canEdit || k.own;
  return (
    <TableRow sx={stopped ? { '& td': { color: 'text.disabled' } } : undefined}>
      <TableCell>{k.label}</TableCell>
      <TableCell>
        {k.created_by}
        <br />
        {madeOn(k.created_at)}
      </TableCell>
      <TableCell>{lastUsed(k.last_used_at, k.last_used_via)}</TableCell>
      <TableCell align="right">
        {stopped ? (
          stoppedText(k.revoked_reason)
        ) : (
          <Tooltip title={mayBlock ? '' : MAKE.onlyAdmins}>
            <span>
              <Button size="small" color="error" disabled={!mayBlock} onClick={() => onBlock(k)}>
                Block
              </Button>
            </span>
          </Tooltip>
        )}
      </TableCell>
    </TableRow>
  );
}

export default function AiAssistantsTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const retailerParam = searchParams.get('retailer') || undefined;

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [brand, setBrand] = useState(null);
  const [newKey, setNewKey] = useState(null); // the Make key, shown once
  const [blocking, setBlocking] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setError(null);
    return dashboardAPI
      .getApiKeys(retailerParam)
      .then(setData)
      .catch((e) => setError(e.message));
  }, [retailerParam]);

  useEffect(() => {
    load();
    dashboardAPI
      .getTopStillWantedBrand(retailerParam)
      .then(setBrand)
      .catch(() => setBrand(null));
  }, [load, retailerParam]);

  const run = async (call) => {
    setBusy(true);
    setError(null);
    try {
      await call();
      await load();
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  };

  const createMakeKey = () =>
    run(async () => {
      const row = await dashboardAPI.createApiKey('make', retailerParam);
      track('AI Assistants Make Key Created');
      setNewKey(row);
    });

  const confirmBlock = () =>
    run(async () => {
      await dashboardAPI.blockApiKey(blocking.id, retailerParam);
      track('AI Assistants Key Blocked', { client: blocking.client, own: blocking.own });
      setBlocking(null);
    });

  if (!data && !error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const { active, stopped } = visibleKeys(data?.keys || []);
  const canEdit = Boolean(data?.can_edit);

  return (
    <Stack spacing={2.5}>
      {data?.group?.length > 1 && (
        <TextField
          select
          label="Shop"
          value={data.retailer_id}
          onChange={(e) => setSearchParams(e.target.value ? { retailer: e.target.value } : {})}
          sx={{ maxWidth: 360 }}
        >
          {data.group.map((g) => (
            <MenuItem key={g.retailer_id} value={g.retailer_id}>
              {g.name}
            </MenuItem>
          ))}
        </TextField>
      )}

      <Typography>{INTRO}</Typography>
      {error && <Alert severity="error">{error}</Alert>}

      <Box>
        <Panel title="Connect ChatGPT">
          <Steps steps={CHATGPT_STEPS} />
          {CHATGPT_NOTES.map((n) => (
            <Typography key={n} variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              <Step step={n} />
            </Typography>
          ))}
        </Panel>
        <Panel title="Connect Claude">
          <Steps steps={CLAUDE_STEPS} warning={CLAUDE_WARNING_STEP} />
        </Panel>
      </Box>

      <Card>
        <CardContent>
          <Typography variant="h6" component="h2" gutterBottom>
            Try it
          </Typography>
          <Stack spacing={0.5}>
            {tryQuestions(brand).map((q) => (
              <Box key={q} sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography>“{q}”</Typography>
                <CopyButton text={q} label="Copy the question" />
              </Box>
            ))}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            {TRY_NOTE}
          </Typography>
        </CardContent>
      </Card>

      <Panel title="Make">
        <Typography sx={{ mb: 1.5 }}>{MAKE.intro}</Typography>
        <Tooltip title={canEdit ? '' : MAKE.onlyAdmins}>
          <span>
            <Button variant="contained" disabled={!canEdit || busy} onClick={createMakeKey}>
              {MAKE.create}
            </Button>
          </span>
        </Tooltip>
        <Typography variant="body2" sx={{ mt: 2 }}>
          <Step step={MAKE.keyHow} />
        </Typography>
        {MAKE.recipes.map((r) => (
          <Box key={r.title} sx={{ mt: 1.5 }}>
            <Typography sx={{ fontWeight: 600 }}>{r.title}</Typography>
            <Typography variant="body2">
              <Code>{r.url}</Code>
              <CopyButton text={r.url} label="Copy the URL" /> Parameter: <Code>{r.params}</Code>
            </Typography>
          </Box>
        ))}
      </Panel>

      <Card>
        <CardContent>
          <Typography variant="h6" component="h2" gutterBottom>
            Keys
          </Typography>
          {active.length + stopped.length === 0 ? (
            <Typography color="text.secondary">No keys yet. Connect ChatGPT or Claude above.</Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Made by</TableCell>
                    <TableCell>Last used</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...active, ...stopped].map((k) => (
                    <KeyRow key={k.id} k={k} canEdit={canEdit} onBlock={setBlocking} />
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>

      <Typography variant="body2" color="text.secondary">
        {HELP}
      </Typography>

      <Dialog open={Boolean(newKey)} onClose={() => setNewKey(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Your Make key</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {MAKE.shownOnce}
          </Alert>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Code>{newKey?.key}</Code>
            <CopyButton text={newKey?.key || ''} label="Copy the key" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewKey(null)}>Done</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(blocking)} onClose={() => setBlocking(null)}>
        <DialogContent>{blocking && blockQuestion(blocking.label)}</DialogContent>
        <DialogActions>
          <Button onClick={() => setBlocking(null)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={busy} onClick={confirmBlock}>
            Block
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
